import { useEffect, useState, memo } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { Body } from "@vds/typography";

import { Icon } from "@vds/icons";
import { Input } from "@vds/inputs";
import { DropdownSelect } from "@vds/selects";
import { Checkbox } from "@vds/checkboxes";
import { Badge } from "@vds/badges";
import { Tooltip } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import { useEnableSearchState } from "../../hooks/useEnableSearchState";
import { useHistoricalSearchEligibility } from "../../hooks/useHistoricalSearchEligibility";
import {
  disableNextButton,
  getFovIds,
  selectEncodes,
  setPassword,
  setUserName,
} from "../../../../store/actions";
import { encodingConstants, resolutionConstants } from "../constants";
import { 
  RTSP_VALIDATION_CONSTANTS, 
  validateRtspUrl, 
  validateCredentialsMatch 
} from "../../../../constants/rtspValidationConstants";
import { encodeOrDecodeRtspCredentials } from "../../../../global/utility/Utils";
import { StreamInfoFields, StreamInfoRoot } from "../styles";
import { safeDecodeURIComponent } from "../../../../global/utility/devicesUtils";

const StreamInfoForm = ({ onNextDisableChange, srollchange, onClose }) => {
  // Targeted selector with shallowEqual — prevents re-render when unrelated fields (e.g. cameraName) change in Redux
  const {
    streamInfo,
    rtspUrl,
    userHostName,
    userPortName,
    modalOpenObj,
    manualAddResponse,
    manualAddStatus,
    userFullName,
    userPassword,
    calculatedAvailabilityCount,
    camDetails
  } = useSelector(({ cameraOnboardingReducer: r }) => ({
    streamInfo: r.streamInfo,
    rtspUrl: r.rtspUrl,
    userHostName: r.userHostName,
    userPortName: r.userPortName,
    modalOpenObj: r.modalOpenObj,
    manualAddResponse: r.manualAddResponse,
    manualAddStatus: r.manualAddStatus,
    userFullName: r.userFullName,
    userPassword: r.userPassword,
    calculatedAvailabilityCount: r.calculatedAvailabilityCount,
    camDetails: r.camDetails,
  }), shallowEqual);
  const { isCameraOffline, effectiveAvailable, notCalibratedFovIds, calibratingFovIds } = useEnableSearchState({
    editFlow: modalOpenObj?.editFlow,
    cameraId: camDetails?.id,
    enabledItems: streamInfo,
  });
  const isHistoricalSearchEligible = useHistoricalSearchEligibility(camDetails?.parentdeviceid);
  const dispatch = useDispatch();
  const currentRtspUrl = rtspUrl;

  const [inputFields, setInputFields] = useState([
    { url: currentRtspUrl, name: "", encoding: "", resolution: "" },
  ]);
  const [prevHost, setPrevHost] = useState(userHostName);
  const [prevPort, setPrevPort] = useState(userPortName);
  const [errorText, setErrorText] = useState("");
  const [rtspErrors, setRtspErrors] = useState({});


  const extractPathFromUrl = (url) => {
    if (!url) return "";
    const parts = url.split("://");
    if (parts?.length < 2) return "";
    const urlParts = parts[1].split("/");
    if (urlParts?.length < 2) return "";
    return "/" + urlParts?.slice(1)?.join("/");
  };

  const buildRtspUrlWithPath = (
    username,
    password,
    host,
    port,
    originalUrl
  ) => {
    if (!username || !password || !host) {
      return originalUrl || "";
    }
    const existingPath = extractPathFromUrl(originalUrl);
    const encodedUsername = encodeURIComponent(username);
    const encodedPassword = encodeURIComponent(password);
    if (!port || port === "") {
      return `rtsp://${encodedUsername}:${encodedPassword}@${host}${existingPath}`;
    }
    return `rtsp://${encodedUsername}:${encodedPassword}@${host}:${port}${existingPath}`;
  };
  useEffect(() => {
    const hostChanged = userHostName !== prevHost;
    const portChanged = userPortName !== prevPort;
    if (streamInfo && streamInfo.length > 0  && (hostChanged || portChanged || userFullName || userPassword)) {
      // && 
      //   streamInfo.some(item => item?.urlRtsp && item.urlRtsp !== "")) {
      const updatedData = streamInfo?.map((item) => ({
        ...item,
        urlRtsp: buildRtspUrlWithPath(
          userFullName,
          userPassword,
          userHostName,
          userPortName,
          item?.urlRtsp || ''
        ),
        rtspPath: item?.rtspPath || extractPathFromUrl(item?.urlRtsp)
      }));

      setInputFields(updatedData);
      dispatch(selectEncodes(updatedData));
      setPrevHost(userHostName);
      setPrevPort(userPortName);
    }
  }, [userHostName, userPortName, userFullName, userPassword]);


  useEffect(() => {
    setInputFields(
      streamInfo && streamInfo.length > 0
        ? streamInfo
        : [{ url: "", name: "", encoding: "", resolution: "", urlRtsp: "", rtspPath: "" }]
    );
  }, [streamInfo, rtspUrl]);

  const updatedRtspUrl = (index) => {
    const currentStreamInfo = streamInfo[index];
    const fovsurl = currentStreamInfo?.urlRtsp || currentStreamInfo?.url;
    
    // Check if fovsurl is a string before processing
    if (fovsurl && typeof fovsurl === 'string' && fovsurl !== "") {
      // Validate RTSP URL format
    }
  };

  useEffect(() => {
    setPrevHost(userHostName);
    setPrevPort(userPortName);
  }, []); // Only run on mount

  useEffect(() => {
    handleValidation();
  }, []);

  const handleFormChange = (index, event) => {
    event.preventDefault();
    let data = [...streamInfo];
    
    if (event.target.name === "rtspPath") {
      const sanitizedPath = event.target.value;

      if (sanitizedPath && /%/.test(sanitizedPath)) {
        setErrorText(RTSP_VALIDATION_CONSTANTS.ERRORS.INVALID_PATH_ENCODING);
        data[index][event.target.name] = sanitizedPath;
        setInputFields(data);
        onNextDisableChange(true);
        return;
      } else {
        setErrorText("");
      }

      const encodedUsername = encodeURIComponent(userFullName);
      const encodedPassword = encodeURIComponent(userPassword);
      const base = userPortName
        ? `rtsp://${encodedUsername}:${encodedPassword}@${userHostName}:${userPortName}`
        : `rtsp://${encodedUsername}:${encodedPassword}@${userHostName}`;
      const rawPath = !sanitizedPath
        ? ""
        : sanitizedPath.startsWith('/')
        ? sanitizedPath
        : `/${sanitizedPath}`;

      data[index]['urlRtsp'] = base + rawPath;
      data[index][event.target.name] = sanitizedPath;
      setInputFields(data);
    
    } else {
      data[index][event.target.name] = event.target.value;
      setInputFields(data);
    }
    
    handleValidation();
    dispatch(selectEncodes(data));
    
    if (event.target.name === "name") {
      const updatedFovIds = data.map((item, idx) => ({
        fovId: item?.fovId,
        fovName: item.name || item.fovName || "",
      }));
      dispatch(getFovIds(updatedFovIds));
    }
  };

  // Helper function to safely get the display value
  const getDisplayValue = (input) => {
    const url = input?.urlRtsp;
    if (!url || typeof url !== 'string') return "";
    try {
      // Try to decode the URL for display, but handle errors gracefully
      return encodeOrDecodeRtspCredentials(url);
    } catch (error) {
      console.warn("Error decoding URL for display:", error);
      return url;
    }
  };

  const addNewFields = (e) => {
    e.preventDefault();
    let newfield = {
      url: currentRtspUrl,
      name: "",
      encoding: "",
      resolution: "",
      rtspPath: "",
      enableSearch: false,
      urlRtsp: buildRtspUrlWithPath(
          userFullName,
          userPassword,
          userHostName,
          "",
          '/'
        )
    };
    setInputFields([...streamInfo, newfield]);
    dispatch(selectEncodes([...(streamInfo || []), newfield]));
    srollchange();
  };

  const deleteFields = (e, index) => {
    e.preventDefault();
    streamInfo?.splice(index, 1);
    setInputFields([...streamInfo]);
    
    // Remove error for deleted field
    setRtspErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[index];
      // Reindex remaining errors
      const reindexedErrors = {};
      Object.keys(newErrors).forEach(key => {
        const oldIndex = parseInt(key);
        if (oldIndex > index) {
          reindexedErrors[oldIndex - 1] = newErrors[key];
        } else {
          reindexedErrors[oldIndex] = newErrors[key];
        }
      });
      return reindexedErrors;
    });
    
    handleValidation();
    dispatch(selectEncodes([...streamInfo]));
  };

  const handleValidation = () => {
    let isValid = false;
    let hasRtspErrors = false;

    inputFields.forEach((item, idx) => {
      if (
        // !item.name ||
        !item.fovName ||
        !(item.urlRtsp ? item?.urlRtsp : item?.url) ||
        !item.encoding ||
        !item.resolution
      ) {
        isValid = true;
      }

      // Check for RTSP URL validation errors
      const rtspUrl = item.urlRtsp || item.url;
      if (rtspUrl) {
        const formatError = validateRtspUrl(rtspUrl);
        const credentialsError = validateCredentialsMatch(rtspUrl, userFullName, userPassword);
        if (formatError || credentialsError) {
          hasRtspErrors = true;
          isValid = true;
        }
      }
    });

    if ((isValid || hasRtspErrors) && modalOpenObj?.editFlow == false) {
      dispatch(disableNextButton(true));
    }
  };

  // Re-validate when credentials change
  useEffect(() => {
    if (streamInfo && streamInfo.length > 0) {
      handleValidation();
    }
  }, [userFullName, userPassword]);

  useEffect(() => {
    return () => {
      setInputFields([{ url: "", name: "", encoding: "", resolution: "", rtspPath: "" }]);
      setRtspErrors({});
    };
  }, [onClose, dispatch]);

  return (
    <>
    <StreamInfoRoot>
      <StreamInfoFields>
        {streamInfo &&
          streamInfo?.map((input, index) => {
            return (
              <div
                key={index}
                style={{
                  marginTop: "20px",
                  backgroundColor: "#F6F6F6",
                  padding: "12px",
                  gap: "24px",
                  borderRadius: "4px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    gap: "20.5rem",
                  }}
                >
                  <Body
                    size="large"
                    viewport="desktop"
                    primitive="span"
                    color="#000000"
                    surface="dark"
                    style={{ backgroundColor: "#F6F6F6" }}
                  >
                    <strong>{`Stream ${streamInfo.length === 1 ? "" : index + 1
                      } info`}</strong>
                  </Body>
                  {index !== 0 && (
                    <div style={{ float: "right", cursor: "pointer" }}>
                      <div
                        onClick={(e) => {
                          deleteFields(e, index);
                        }}
                      >
                        {" "}
                        <Icon name="trash" color="#000000" size="large" />{" "}
                      </div>
                    </div>
                  )}
                </div>
                {modalOpenObj?.editFlow && isHistoricalSearchEligible && (
                <div style={{ marginTop: "16px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0" }}>
                    <div style={{ width: "fit-content" }} className="enable-search-checkbox">
                      <Checkbox
                        label="Enable Search"
                        selected={!!input?.enableSearch}
                        disabled={isCameraOffline || notCalibratedFovIds.has(input?.fovId) || calibratingFovIds.has(input?.fovId) || (effectiveAvailable === 0 && !input?.enableSearch)}
                        onChange={() => {
                          const data = [...streamInfo];
                          data[index] = { ...data[index], enableSearch: !data[index].enableSearch };
                          dispatch(selectEncodes(data));
                        }}
                      />
                    </div>
                    <Tooltip title="Enabling Search will activate data collection on this stream, allowing you to search footage from this camera." placement="top" PopperProps={{ disablePortal: true }}>
                      <InfoOutlined fontSize="small" style={{ color: "#6f7171", cursor: "pointer", marginLeft: "5px", marginBottom: "4px" }} />
                    </Tooltip>
                  </div>
                  {effectiveAvailable !== null && (
                    <Body size="small" viewport="desktop" primitive="span" color="#6f7171" style={{ marginBottom: "0px", whiteSpace: "nowrap" }}>
                      {effectiveAvailable === 1 ? "1 license is available" : `${effectiveAvailable} licenses are available`}
                    </Body>
                  )}
                  {(isCameraOffline || notCalibratedFovIds.has(input?.fovId) || calibratingFovIds.has(input?.fovId)) && (
                    <div style={{ marginTop: "4px" }}>
                      <Badge fillColor="yellow">
                        {isCameraOffline
                          ? "Camera is Offline"
                          : notCalibratedFovIds.has(input?.fovId)
                          ? "Camera is Not Calibrated"
                          : "Camera is Calibrating"}
                      </Badge>
                    </div>
                  )}
                </div>
                )}
                {input?.urlRtsp && (
                  <div style={{ marginTop: "20px" }}>
                   <Body
                    size="small"
                    viewport="desktop"
                    primitive="span"
                    color="#000000"
                    surface="dark"
                    style={{ backgroundColor: "#F6F6F6" }}
                  >RTSP URL{" "}</Body>
                  <Body size="small" viewport="desktop" primitive="span"
                    color="#6f7171"
                    surface="light"
                    style={{ backgroundColor: "#F6F6F6" }}
                  >{safeDecodeURIComponent(input?.urlRtsp !== "" ? input?.urlRtsp : input?.url)}</Body>
                </div>)}
                <div style={{ marginTop: "20px" }} >
                  <Input
                    type="text"
                    label="Stream Path"
                    name="rtspPath"
                    placeholder=""
                    required={false}
                    value={input?.rtspPath}
                    width="100%"
                    helperText={RTSP_VALIDATION_CONSTANTS.HELPER_TEXT_PATH}
                    helperTextPlacement="bottom"
                    errorText={errorText}
                    error={errorText ? true : false}
                    onChange={(event) => {
                      handleFormChange(index, event);
                    }}
                    onClick={(e) => {
                      // Prevent scroll jump on click
                      e.stopPropagation();
                    }}
                  />
                </div>
                <div style={{ marginTop: "20px" }}>
                  <Input
                    type="text"
                    label="Stream name"
                    name="name"
                    placeholder=""
                    required={true}
                    defaultValue={input?.fovName ? input?.fovName : input?.name}
                    width="100%"
                    helperText="How the stream will appear with in the 5G Video Insights portal"
                    helperTextPlacement="bottom"
                    onChange={(event) => handleFormChange(index, event)}
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    flex: "2",
                    gap: "10px",
                    paddingTop: "10px",
                  }}
                >
                  <DropdownSelect
                    label="Encoding"
                    name="encoding"
                    error={false}
                    required={true}
                    width="50%"
                    value={input?.encoding || ""}
                    onChange={(event) => handleFormChange(index, { preventDefault: () => {}, target: { name: "encoding", value: event.target.value } })}
                  >
                    <option value="">Select Encoding</option>
                    {Object.keys(encodingConstants).map((key) => (
                      <option key={key} value={key}>
                        {encodingConstants[key]}
                      </option>
                    ))}
                  </DropdownSelect>
                  <DropdownSelect
                    label="Resolution"
                    name="resolution"
                    error={false}
                    required={true}
                    width="50%"
                    helperText="Preferred: 4CIF(SD) - 704x480"
                    helperTextPlacement="bottom"
                    value={input?.resolution}
                    onChange={(event) => handleFormChange(index, { preventDefault: () => {}, target: { name: "resolution", value: event.target.value } })}
                  >
                    <option value="">Select Resolution</option>
                    {Object.keys(resolutionConstants).map((key) => (
                      <option key={key} value={`${resolutionConstants[key]}`}>
                        {resolutionConstants[key]}
                      </option>
                    ))}
                  </DropdownSelect>
                </div>
              </div>
            );
          })}
        {(modalOpenObj?.editFlow === false ||
          modalOpenObj?.isPairing === true) &&
          manualAddStatus === "NOT_INTIATED" &&
          streamInfo?.length < calculatedAvailabilityCount && (
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  paddingTop: "20px",
                  paddingBottom: "10px",
                }}
              >
                <Body
                  size="small"
                  bold
                  viewport="desktop"
                  primitive="span"
                  color="#000000"
                  surface="dark"
                >
                  For multi-stream cameras, you may add additional streams as
                  needed.
                </Body>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  paddingTop: "20px",
                  cursor: "pointer"
                }}
                onClick={(e) => {
                  addNewFields(e);
                }}
              >
                <Icon name="plus" color="#000000" size="large" />
                <Body size="large" bold viewport="desktop" primitive="span" color="#000000" surface="dark">
                  <strong>Add another stream to camera</strong>
                </Body>
              </div>
            </>
          )}
      </StreamInfoFields>
    </StreamInfoRoot>
    </>
  );
};

// memo — prevents re-render when parent (DiscoverCamera) re-renders due to unrelated state changes
export default memo(StreamInfoForm);
